import { createTheme } from "@dash/default-theme";
import { Theme, ThemeProvider } from "@emotion/react";
import { NewRouterBreadcrumbsBar } from "@legacy-megarax/ui-components";
import { TradeRouteDetailsContainer, TradeRoutesHomeContainer } from "@megarax/crm-customer-map";
import {
  AddCustomerPage,
  CustomerPage,
  CustomersViewPage,
  LegacyMegaraxPricingRulesSyncDiffPage,
  PricingRulesSyncDiffPage,
} from "@megarax/crm-customers";
import { MyAffiliateCodePage, MyAffiliateCodesPage } from "@megarax/crm-my-affiliates-codes";
import { PriceReportFormContainer } from "@megarax/crm-my-price-reports";
import { OrderDraftPage, OrderDraftsPage, OrderPage, OrdersPage } from "@megarax/crm-orders";
import { PostSaleDiscountsPage } from "@megarax/crm-post-sale-discounts";
import { SubjectsViewContainer } from "@megarax/crm-price-reports";
import { ProductAvailabilityHome } from "@megarax/crm-product-availability";
import { ProfitTargetsPage } from "@megarax/crm-profit-targets";
import { RegionPage, RegionsHome, SalespersonRedirect } from "@megarax/crm-regions";
import {
  BcItemSyncDiffPage,
  LegacyMegaraxPriceSyncDiffPage,
  PriceListsPage,
  SheetsItemSyncDiffPage,
  SheetsPriceListSyncDiffPage,
  SheetsPriceSyncDiffPage,
  ShopifyPriceSyncDiffPage,
  ShopifyProductSyncDiffPage,
  ShopifyVariantSyncDiffPage,
  TradeItemsPage,
} from "@megarax/crm-trade-items";
import { TrainingHome } from "@megarax/crm-training";
import { UserHomepage } from "@megarax/crm-user";
import {
  DayViewContainer,
  MyVisitsRedirect,
  VisitRedirect,
  VisitsRedirect,
  WeekViewContainer,
} from "@megarax/crm-visits";
import { useConfig } from "@megarax/dev-config";
import { MegaraxWebappContainer } from "@megarax/features";
import { HomeView, MegaraxProviders } from "@megarax/root";
import { MdGroups, MdOutlineInventory2 } from "react-icons/md";
import { Navigate, Outlet, Route, useParams } from "react-router-dom-v5-compat";

import { getMegaraxPath } from "@megaron/megarax-uri";

import { NavigateKeepingSearch } from "./NavigateKeepingSearch";

const crmTheme: Theme = createTheme("273");
const catalogTheme: Theme = createTheme("150");

export const useCrmRouter = () => {
  return (
    <>
      <Route path="/catalog" element={<CatalogRouteContainer />}>
        <Route path="" element={<Navigate to={getMegaraxPath.uri("megarax://catalog/items")} replace />} />
        <Route path="items">
          <Route path="" element={<TradeItemsPage />} />
          <Route path="id/:itemId/*" element={<ItemRedirect />} />
        </Route>
        <Route path="price-lists" element={<PriceListsPage />} />
        <Route path="diff-sync/bcItem" element={<BcItemSyncDiffPage />} />
        <Route path="diff-sync/sheetsItem" element={<SheetsItemSyncDiffPage />} />
        <Route path="diff-sync/sheetsPriceList" element={<SheetsPriceListSyncDiffPage />} />
        <Route path="diff-sync/sheetsPrice" element={<SheetsPriceSyncDiffPage />} />
        <Route path="diff-sync/shopifyProduct" element={<ShopifyProductSyncDiffPage />} />
        <Route path="diff-sync/shopifyVariant" element={<ShopifyVariantSyncDiffPage />} />
        <Route path="diff-sync/shopifyPrice" element={<ShopifyPriceSyncDiffPage />} />
        <Route path="diff-sync/legacyMegaraxPrice" element={<LegacyMegaraxPriceSyncDiffPage />} />
      </Route>

      <Route path="/crm" element={<CrmRouteContainer />}>
        <Route path="" element={<Navigate to={getMegaraxPath.uri("megarax://crm/my-feed")} replace />} />
        <Route path="home" element={<UserHomepage />} />

        <Route path="diff-sync/sheetsPricingRule" element={<PricingRulesSyncDiffPage />} />

        <Route path="diff-sync/legacyMegaraxPricingRule" element={<LegacyMegaraxPricingRulesSyncDiffPage />} />

        <Route path="customers">
          <Route
            path=""
            element={<NavigateKeepingSearch toPathname={getMegaraxPath.uri("megarax://crm/customers/new-list")} />}
          />
          <Route path="new-list/*" element={<CustomersViewPage template="list" />} />
          <Route path="new-map/*" element={<CustomersViewPage template="map" />} />
          <Route path="new-customer-analytics/*" element={<CustomersViewPage template="analytics" />} />
          <Route path="new/*" element={<AddCustomerPage />} />
          <Route path="id/:customerId/*" element={<CustomerPage />} />
        </Route>

        <Route path="customer-lists">
          <Route path="id/:viewId/*" element={<CustomersViewPage />} />
        </Route>

        <Route path="regions/*">
          <Route path="*" element={<RegionsHome />} />
          <Route path="id/:region/*" element={<RegionPage />} />
        </Route>

        <Route path="product-availability/*" element={<ProductAvailabilityHome />} />
        <Route path="training-calendar/*" element={<TrainingHome />} />
        <Route path="post-sale-discounts" element={<PostSaleDiscountsPage />} />
        <Route path="profit-targets" element={<ProfitTargetsPage />} />

        <Route path="orders">
          <Route path="" element={<OrdersPage />} />
          <Route path="number/:number" element={<OrderPage />} />
        </Route>

        <Route path="order-drafts">
          <Route path="" element={<OrderDraftsPage />} />
          <Route path="new/*" element={<OrderDraftPage isNew />} />
          <Route path="id/:orderId/*" element={<OrderDraftPage />} />
        </Route>

        <Route path="my-affiliate-codes">
          <Route path="" element={<MyAffiliateCodesPage />} />
          <Route path="id/:affiliateCodeId/*" element={<MyAffiliateCodePage />} />
        </Route>

        <Route element={<LegacyCrmMegaraxWebappContainer />}>
          <Route path="my-feed" element={<HomeView />} />
          <Route path="price-reports" element={<PriceReportFormContainer />} />
          <Route path="price-report-products" element={<SubjectsViewContainer />} />
          <Route path="my-visits" element={<MyVisitsRedirect />} />
          <Route path="visits/id/:uuid" element={<VisitRedirect />} />
          <Route path="salespeople">
            <Route path="" element={<Navigate to={getMegaraxPath.uri("megarax://crm/regions")} replace />} />
            <Route path="id/:userId" element={<SalespersonRedirect />} />
          </Route>
          <Route path="salesperson-visits/id/:userId">
            <Route path="" element={<VisitsRedirect />} />
            <Route path="week">
              <Route path="" element={<VisitsRedirect view="week" />} />
              <Route path=":date/visits/id/:visitId" element={<VisitRedirect />} />
              <Route path=":date/*" element={<WeekViewContainer />} />
            </Route>
            <Route path="day">
              <Route path="" element={<VisitsRedirect view="day" />} />
              <Route path=":date/visits/id/:visitId" element={<VisitRedirect />} />
              <Route path=":date/*" element={<DayViewContainer />} />
            </Route>
          </Route>
          <Route path="trade-routes">
            <Route path="" element={<TradeRoutesHomeContainer />} />
            <Route path="id/:tradeRouteUuid" element={<TradeRouteDetailsContainer />} />
          </Route>
        </Route>
      </Route>
    </>
  );
};

const ItemRedirect = () => {
  const { itemId } = useParams<{ itemId: string }>();

  return (
    <Navigate
      to={
        itemId
          ? getMegaraxPath.uri("megarax://catalog/items", { filters: { searchText: itemId } })
          : getMegaraxPath.uri("megarax://catalog/items")
      }
      replace
    />
  );
};

const CrmRouteContainer = () => (
  <ThemeProvider theme={crmTheme}>
    <CrmMegaraxWebappContainer />
  </ThemeProvider>
);

const CrmMegaraxWebappContainer: React.FC = () => {
  const { config } = useConfig();

  return (
    <MegaraxWebappContainer
      logo={<MdGroups width="1em" height="1em" />}
      title="CRM"
      subtitle="megarax.net"
      bypassAuth={config.testUser.enabled}
    >
      <MegaraxProviders>
        <Outlet />
      </MegaraxProviders>
    </MegaraxWebappContainer>
  );
};

const CatalogRouteContainer = () => (
  <ThemeProvider theme={catalogTheme}>
    <CatalogMegaraxWebappContainer />
  </ThemeProvider>
);

const CatalogMegaraxWebappContainer: React.FC = () => {
  const { config } = useConfig();

  return (
    <MegaraxWebappContainer
      logo={<MdOutlineInventory2 width="1em" height="1em" />}
      title="Oferta"
      subtitle="megarax.net"
      bypassAuth={config.testUser.enabled}
    >
      <MegaraxProviders>
        <Outlet />
      </MegaraxProviders>
    </MegaraxWebappContainer>
  );
};

const LegacyCrmMegaraxWebappContainer = () => {
  return (
    <MegaraxProviders>
      <NewRouterBreadcrumbsBar />
      <Outlet />
    </MegaraxProviders>
  );
};
